import { Children, type ReactNode } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import { lelyTokenPrimitives } from '@/common/styles/newStyles/LelyMuiTheme';

/** Matches `variables.$header-height`. */
const appHeaderHeight = lelyTokenPrimitives.space7xl;

/** `main` shell marker — used by overlays (e.g. ToggleTip) to stay inside the page area. */
export const PAGE_LAYOUT_SELECTOR = '[data-page-layout="shell"]';

export function getPageLayoutContainer(): HTMLElement {
  return (
    document.querySelector<HTMLElement>(PAGE_LAYOUT_SELECTOR) ?? document.body
  );
}

export function getPageLayoutBounds(margin = 0) {
  const pageLayout = getPageLayoutContainer();
  const rect = pageLayout.getBoundingClientRect();

  return {
    minLeft: rect.left + margin,
    maxRight: rect.right - margin,
    minTop: rect.top + margin,
    maxBottom: rect.bottom - margin,
  };
}

const shellSx: SxProps<Theme> = {
  boxSizing: 'border-box',
  width: '100%',
  p: lelyTokenPrimitives.spaceSm,
  overflowX: 'hidden',
  minWidth: 0,
  height: `calc(100vh - ${appHeaderHeight})`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Explicit height so that CSS Grid `fr` units inside the row resolve correctly.
 * `height: '100%'` alone is unreliable for grid fr resolution when the parent
 * height comes from a flex algorithm rather than an explicit property.
 */
const fillAvailableHeightSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  height: `calc(100vh - ${appHeaderHeight} - 2 * ${lelyTokenPrimitives.spaceSm})`,
};

/** Horizontal panels: clip overflow so grid columns do not spill past the shell. */
const fillRowSx: SxProps<Theme> = {
  ...fillAvailableHeightSx,
  overflow: 'hidden',
};

type PageLayoutProps = {
  children: ReactNode;
  testId?: string;
  className?: string;
  sx?: SxProps<Theme>;
};

/**
 * Page shell: theme padding, viewport height below app header.
 * Pair with `PageRow` (horizontal panels) or `PageStack` (toolbar + main content).
 */
function PageLayout({ children, testId, className, sx }: PageLayoutProps) {
  return (
    <Box
      component="main"
      data-page-layout="shell"
      data-testid={testId}
      className={className}
      sx={[shellSx, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])]}
    >
      {children}
    </Box>
  );
}

const buildStackRows = (
  childCount: number,
  rows?: string,
  fillLastRow = true,
) => {
  if (rows) {
    return rows;
  }
  if (!fillLastRow) {
    return childCount > 0 ? `repeat(${childCount}, auto)` : 'auto';
  }
  if (childCount <= 1) {
    return '1fr';
  }
  return `${'auto '.repeat(childCount - 1)}1fr`.trim();
};

type PageStackProps = {
  children: ReactNode;
  /**
   * CSS `grid-template-rows`. When omitted, uses `auto` for each child except the
   * last, which receives `1fr` (see `fillLastRow`).
   */
  rows?: string;
  /**
   * When true (default), the last child row grows to fill remaining viewport height.
   * @default true
   */
  fillLastRow?: boolean;
  sx?: SxProps<Theme>;
};

/**
 * Vertical stack for toolbar/header row(s) plus a main area that fills remaining height.
 *
 * - 2 children (Settings): `auto 1fr`
 * - 3+ children (Farms): `auto auto … 1fr`
 */
export function PageStack({
  children,
  rows,
  fillLastRow = true,
  sx,
}: PageStackProps) {
  const childCount = Children.count(children);
  const gridTemplateRows = buildStackRows(childCount, rows, fillLastRow);

  return (
    <Box
      sx={[
        {
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gridTemplateRows,
          gap: lelyTokenPrimitives.spaceSm,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          minHeight: 0,
          height: '100%',
          boxSizing: 'border-box',
          '& > *': {
            minWidth: 0,
            maxWidth: '100%',
          },
        },
        fillAvailableHeightSx,
        fillLastRow && {
          '& > *:last-child': {
            minHeight: 0,
          },
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      {children}
    </Box>
  );
}

type PageRowProps = {
  children: ReactNode;
  /** CSS `grid-template-columns` value, e.g. `"auto 1fr"` or `"minmax(0,3fr) minmax(0,10fr)"`. */
  columns?: string;
  sx?: SxProps<Theme>;
};

/** Horizontal CSS Grid row for multi-panel pages (filter | main | optional details). */
export function PageRow({ children, columns, sx }: PageRowProps) {
  return (
    <Box
      sx={[
        {
          display: 'grid',
          gridTemplateColumns: columns,
          gap: lelyTokenPrimitives.spaceSm,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          '& > *': {
            minWidth: 0,
            maxWidth: '100%',
            minHeight: 0,
            maxHeight: '100%',
          },
        },
        fillRowSx,
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      {children}
    </Box>
  );
}

export default PageLayout;
